import { faviconSvg } from './brandMark';

// Doit rester aligné avec server/utils/personalization.ts
export const ALLOWED_FONTS = [
  'Playfair Display',
  'Cormorant Garamond',
  'Great Vibes',
  'Montserrat',
  'Lora',
  'Dancing Script',
  'Raleway',
];

export const DEFAULT_COLOR = '#b76e79';

export interface Personalization {
  fontFamily?: string | null;   // police des titres
  bodyFont?: string | null;     // police du texte
  primaryColor?: string | null;
  heroImage?: string | null;
  heroImagePositionY?: number | null; // cadrage vertical 0-100 (null = centré)
  weddingDate?: string | null;  // AAAA-MM-JJ
  venueName?: string | null;
  venueAddress?: string | null;
}

// Formate une date "AAAA-MM-JJ" (sans décalage de fuseau)
export function formatWeddingDate(value?: string | null, locale = 'fr-FR'): string | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const formatted = d.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

// Charge (une seule fois) toutes les polices proposées depuis Google Fonts,
// pour pouvoir les prévisualiser et les appliquer.
export function ensureFontsLoaded(): void {
  if (document.getElementById('mkt-google-fonts')) return;
  const families = ALLOWED_FONTS.map(
    (f) => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:wght@400;600;700`,
  ).join('&');
  const link = document.createElement('link');
  link.id = 'mkt-google-fonts';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  document.head.appendChild(link);
}

// Luminance relative (WCAG) d'une couleur hex, ou null si illisible.
function relativeLuminance(hex: string): number | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const ch = (i: number) => lin(parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
}

// Applique la couleur primaire du compte au sous-domaine.
// On pose --primary (+ le texte lisible dessus et le dosage de la version
// « encre ») : tout le thème, accents et neutres, se recalcule via color-mix
// (voir styles/theme.css).
// color null/absent → on retombe sur la couleur du site définie dans :root.
export function applyPrimaryColor(color?: string | null): void {
  const root = document.documentElement;
  const lum = color ? relativeLuminance(color) : null;
  if (color && lum !== null) {
    // Texte noir ou blanc : celui qui contraste le mieux avec la couleur
    const onWhite = 1.05 / (lum + 0.05);
    const onDark = (lum + 0.05) / 0.062;
    root.style.setProperty('--primary', color);
    root.style.setProperty('--on-primary', onWhite >= onDark ? '#fff' : '#1c1b1a');
    // Couleur claire → version « encre » nettement assombrie pour rester lisible
    root.style.setProperty('--ink-pct', lum > 0.2 ? '55%' : '80%');
  } else {
    root.style.removeProperty('--primary');
    root.style.removeProperty('--on-primary');
    root.style.removeProperty('--ink-pct');
  }
}

// Applique les polices du couple à toute l'app (variables CSS)
export function applyFonts(heading?: string | null, body?: string | null): void {
  const root = document.documentElement;
  if (heading) root.style.setProperty('--font-heading', `'${heading}'`);
  else root.style.removeProperty('--font-heading');
  if (body) root.style.setProperty('--font-body', `'${body}'`);
  else root.style.removeProperty('--font-body');
}

// Met à jour le favicon (pastille aux alliances) à la couleur donnée
export function setFavicon(color?: string | null): void {
  const c = color || DEFAULT_COLOR;
  const svg = faviconSvg(c);
  const href = 'data:image/svg+xml,' + encodeURIComponent(svg);
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    document.head.appendChild(link);
  }
  link.href = href;
}

// Lit un fichier image en data URL base64
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
